import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import apiService from '../services/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string, rememberMe?: boolean) => Promise<void>;
  signup: (username: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  hasRole: (role: string) => boolean;
  isAdmin: boolean;
  sessionTimeout: number | null;
  resetSessionTimeout: () => void;
  refreshAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Session timeout in milliseconds (24 hours to match backend JWT expiration)
const SESSION_TIMEOUT = 24 * 60 * 60 * 1000;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionTimeout, setSessionTimeout] = useState<number | null>(null);

  const isAuthenticated = !!user;
  const isAdmin = user?.roles?.some(role => 
    (typeof role === 'string' && role === 'ROLE_ADMIN') || 
    (typeof role === 'object' && role.name === 'ROLE_ADMIN')
  ) || false;

  const hasRole = (role: string): boolean => {
    return user?.roles?.some(userRole => 
      (typeof userRole === 'string' && userRole === role) || 
      (typeof userRole === 'object' && userRole.name === role)
    ) || false;
  };

  const resetSessionTimeout = () => {
    if (isAuthenticated) {
      const timeout = Date.now() + SESSION_TIMEOUT;
      setSessionTimeout(timeout);
      localStorage.setItem('sessionTimeout', timeout.toString());
    }
  };

  const checkSessionTimeout = () => {
    const storedTimeout = localStorage.getItem('sessionTimeout');
    if (storedTimeout) {
      const timeout = parseInt(storedTimeout);
      if (Date.now() > timeout) {
        // Session expired
        logout();
        return;
      }
      setSessionTimeout(timeout);
    }
  };

  const setupActivityListeners = () => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    const resetTimeout = () => {
      if (isAuthenticated) {
        resetSessionTimeout();
      }
    };

    events.forEach(event => {
      document.addEventListener(event, resetTimeout, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetTimeout, true);
      });
    };
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (token && refreshToken) {
        try {
          const response = await apiService.getUserProfile();
          setUser(response.data);
          checkSessionTimeout();
          resetSessionTimeout();
          setIsLoading(false);
        } catch (error: any) {
          console.error('Failed to get user profile:', error);
          
          // If it's a 401 error, try to refresh the token
          if (error.response?.status === 401) {
            try {
              console.log('Attempting to refresh token during initialization...');
              const refreshResponse = await apiService.refreshToken(refreshToken);
              const { token: newToken } = refreshResponse.data;
              
              localStorage.setItem('accessToken', newToken);
              
              // Try to get user profile again with new token
              const userResponse = await apiService.getUserProfile();
              setUser(userResponse.data);
              checkSessionTimeout();
              resetSessionTimeout();
              setIsLoading(false);
              return;
            } catch (refreshError) {
              console.error('Token refresh failed during initialization:', refreshError);
            }
          }
          
          // If refresh failed or other error, clear everything
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          localStorage.removeItem('sessionTimeout');
          setUser(null);
          setIsLoading(false);
        }
      } else {
        // No tokens found, user is not authenticated
        setUser(null);
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const cleanup = setupActivityListeners();
      return cleanup;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (sessionTimeout) {
      const interval = setInterval(() => {
        if (Date.now() > sessionTimeout) {
          logout();
        }
      }, 60000); // Check every minute

      return () => clearInterval(interval);
    }
  }, [sessionTimeout]);

  const login = async (username: string, password: string, rememberMe = false) => {
    try {
      const response = await apiService.login({ username, password });
      const { token, refreshToken, ...loginUserData } = response.data;
      
      localStorage.setItem('accessToken', token);
      localStorage.setItem('refreshToken', refreshToken);
      
      // Transform the login response to match the User interface
      // Convert string roles to Role objects and add missing properties
      const userData: User = {
        id: loginUserData.id,
        username: loginUserData.username,
        email: loginUserData.email,
        fullName: undefined, // Will be filled by getUserProfile if needed
        bio: undefined, // Will be filled by getUserProfile if needed
        profilePhotoUrl: undefined, // Will be filled by getUserProfile if needed
        credits: loginUserData.credits,
        createdAt: new Date().toISOString(), // Default value
        updatedAt: new Date().toISOString(), // Default value
        enabled: true, // Default value
        blocked: false, // Default value
        deleted: false, // Default value
        roles: loginUserData.roles.map((role: string) => ({ id: 0, name: role })) // Transform string roles to Role objects
      };
      
      console.log('Login successful - User data received:', userData);
      console.log('User roles:', userData.roles);
      console.log('Is admin check:', userData.roles?.some((role: any) => 
        (typeof role === 'string' && role === 'ROLE_ADMIN') || 
        (typeof role === 'object' && role.name === 'ROLE_ADMIN')
      ));
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Optionally fetch additional profile data (fullName, bio, profilePhotoUrl) if needed
      // but preserve the roles from login response
      try {
        const profileResponse = await apiService.getUserProfile();
        const profileData = profileResponse.data;
        
        // Merge profile data with login data, but keep roles from login
        const completeUserData: User = {
          ...userData,
          fullName: profileData.fullName,
          bio: profileData.bio,
          profilePhotoUrl: profileData.profilePhotoUrl,
          createdAt: profileData.createdAt,
          updatedAt: profileData.updatedAt,
          enabled: profileData.enabled,
          blocked: profileData.blocked,
          deleted: profileData.deleted
        };
        
        setUser(completeUserData);
        localStorage.setItem('user', JSON.stringify(completeUserData));
        console.log('Profile data merged successfully');
      } catch (profileError) {
        console.log('Could not fetch additional profile data, using login data only');
        // Continue with login data only
      }
      
      // Handle remember me functionality
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
        // Extend session timeout for remembered users
        const extendedTimeout = Date.now() + (7 * 24 * 60 * 60 * 1000); // 7 days
        setSessionTimeout(extendedTimeout);
        localStorage.setItem('sessionTimeout', extendedTimeout.toString());
      } else {
        resetSessionTimeout();
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const signup = async (username: string, email: string, password: string) => {
    try {
      await apiService.signup({ username, email, password });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Signup failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('sessionTimeout');
    setUser(null);
    setSessionTimeout(null);
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const refreshAuth = async (): Promise<boolean> => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        return false;
      }

      const response = await apiService.refreshToken(refreshToken);
      const { token } = response.data;
      
      localStorage.setItem('accessToken', token);
      
      // Get updated user profile
      const userResponse = await apiService.getUserProfile();
      setUser(userResponse.data);
      resetSessionTimeout();
      
      return true;
    } catch (error) {
      console.error('Failed to refresh authentication:', error);
      logout();
      return false;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    signup,
    logout,
    updateUser,
    hasRole,
    isAdmin,
    sessionTimeout,
    resetSessionTimeout,
    refreshAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 